import React, { useState, useRef, useEffect } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { FiCamera, FiImage, FiX, FiRefreshCw, FiCheck } from 'react-icons/fi';

const FORMATOS = [
  'QR_CODE', 'EAN_13', 'EAN_8', 'UPC_A', 'UPC_E', 'CODE_128', 'CODE_39', 'CODE_93',
  'ITF', 'CODABAR',
];

const S = {
  container: { display: 'flex', flexDirection: 'column', gap: '14px', alignItems: 'center' },
  preview: {
    width: '100%',
    maxHeight: '320px',
    borderRadius: '14px',
    objectFit: 'contain',
    background: '#000',
    position: 'relative',
    overflow: 'hidden',
  },
  scannerBox: { width: '100%', maxWidth: '360px', position: 'relative', borderRadius: '14px', overflow: 'hidden', background: '#000' },
  acciones: { display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' },
  btn: {
    display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '11px 18px', borderRadius: '12px',
    border: 'none', fontSize: '14px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
  },
  btnPrimario: { background: '#16a34a', color: '#fff' },
  btnSecundario: { background: '#e8f0fe', color: '#1a73e8' },
  btnCancel: { background: '#f1f5f9', color: '#475569' },
  hint: { fontSize: '13px', color: 'var(--agx-muted)', textAlign: 'center' },
  error: { background: '#fef2f2', color: '#dc2626', padding: '10px 14px', borderRadius: '12px', fontSize: '13px' },
};

export default function BridgeCapture({ tipo, onCompletado, onCancelar }) {
  const [foto, setFoto] = useState(null);
  const [fotoUrl, setFotoUrl] = useState(null);
  const [error, setError] = useState(null);
  const fileCamRef = useRef(null);
  const fileGalRef = useRef(null);
  const readerRef = useRef(null);
  const resueltoRef = useRef(false);

  const handleArchivo = (e) => {
    const f = e.target.files && e.target.files[0];
    if (f) {
      setFoto(f);
      setFotoUrl(URL.createObjectURL(f));
      setError(null);
    }
  };

  const confirmarFoto = () => {
    if (foto) onCompletado({ foto, fotoUrl });
  };

  // Escáner de códigos (QR + códigos de barras) con html5-qrcode.
  useEffect(() => {
    if (tipo !== 'escanear_codigo') return undefined;
    let cancelled = false;
    let reader = null;
    const start = async () => {
      try {
        reader = new Html5Qrcode('bridge-scanner');
        readerRef.current = reader;
        await reader.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 320, height: 140 }, formatsToSupport: FORMATOS },
          (texto) => {
            if (!cancelled && !resueltoRef.current) {
              resueltoRef.current = true;
              onCompletado({ codigo: texto });
            }
          },
          () => {}
        );
      } catch (err) {
        if (!cancelled) setError(err?.message || 'No se pudo acceder a la cámara');
      }
    };
    const timer = setTimeout(start, 200);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      if (readerRef.current) {
        try { readerRef.current.stop().catch(() => {}); } catch (e) {}
        readerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipo]);

  if (tipo === 'escanear_codigo') {
    return (
      <div style={S.container}>
        <div style={S.scannerBox}>
          <div id="bridge-scanner" style={{ width: '100%', minHeight: '220px' }} />
        </div>
        <div style={S.hint}>Apunta al código de barras o QR del producto</div>
        {error && <div style={S.error}>{error}</div>}
        <div style={S.acciones}>
          <button style={{ ...S.btn, ...S.btnCancel }} onClick={onCancelar}><FiX size={16} /> Cancelar</button>
        </div>
      </div>
    );
  }

  // Foto: cámara + galería + previsualización + aceptar.
  if (fotoUrl) {
    return (
      <div style={S.container}>
        <img src={fotoUrl} alt="Previsualización" style={S.preview} />
        <div style={S.acciones}>
          <button style={{ ...S.btn, ...S.btnCancel }} onClick={() => { setFoto(null); setFotoUrl(null); }}>
            <FiRefreshCw size={16} /> Repetir
          </button>
          <button style={{ ...S.btn, ...S.btnPrimario }} onClick={confirmarFoto}>
            <FiCheck size={16} /> Aceptar y enviar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={S.container}>
      <input ref={fileCamRef} type="file" accept="image/*" capture="environment" hidden onChange={handleArchivo} />
      <input ref={fileGalRef} type="file" accept="image/*" hidden onChange={handleArchivo} />
      <div style={S.hint}>Elige cómo capturar la imagen</div>
      <div style={S.acciones}>
        <button style={{ ...S.btn, ...S.btnPrimario }} onClick={() => fileCamRef.current?.click()}>
          <FiCamera size={16} /> Tomar foto
        </button>
        <button style={{ ...S.btn, ...S.btnSecundario }} onClick={() => fileGalRef.current?.click()}>
          <FiImage size={16} /> Galería
        </button>
        <button style={{ ...S.btn, ...S.btnCancel }} onClick={onCancelar}><FiX size={16} /> Cancelar</button>
      </div>
    </div>
  );
}
